import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CloseIcon, Modal } from '@esgian/esgianui';
import CreateBaselineType from '@components/Inputs/BaselineInput/BaselineModal/CreateBaselineType';
import {
  BASELINE_TYPE,
  DISPLAY_UNIT,
  REQUIRED_TARGET_FIELDS,
  UTC_TIMESTAMP
} from '@constants/index';
import BaselineDetails from '@components/Inputs/BaselineInput/BaselineModal/BaselineDetails';
import ManageBaselines from '@components/Inputs/BaselineInput/BaselineModal/ManageBaselines';
import { createBaseline, fetchBaselineById, updateBaseline } from '@api/Baselines';
import { useDispatch, useSelector } from 'react-redux';
import { addBaseline, getNextRankNumber, getUserId, updateBaselineDetails } from '@store/features';
import { toast } from 'react-toastify';
import { setSelectedBaseline } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import Swal from 'sweetalert2';
import { useTheme } from '@hooks';
import moment from 'moment/moment';
import { roundToSixDecimals } from '@helpers/numberFormatter';
import { isNumeric } from '@helpers/helpers';
import {
  getMainPageRig
} from '@store/features/filters/MainPageSlice/MainPageSlice';

const checkValidBaselineDetails = (baseline) => {
  const { selectedRig, startDate, endDate, baselineName } = baseline;
  if (!baselineName) return false;

  if (baseline.baselineTypeId === BASELINE_TYPE.TIME_WELL) {
    if (!selectedRig || !startDate || !endDate || !baselineName) {
      return false;
    }
  }

  return true;
};

function BaselineModal({ handleClose, modalType }) {
  const mainRig = useSelector(getMainPageRig);
  const [stage, setStage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [highlightErrors, setHighlightErrors] = useState(false);
  const [baseline, setBaseline] = useState({
    daily: true,
    baselineTypeId: BASELINE_TYPE.TARGET,
    autoUpdate: true
  });
  let rank = useSelector(getNextRankNumber);
  const userId = useSelector(getUserId);
  const { theme } = useTheme();
  const dispatch = useDispatch();

  const { title, showButtons, btnText } = useMemo(() => {
    let tempTitle = '';
    let tempShowButtons = true;
    let tempBtnText = 'Save';

    if (stage === 1 && modalType === 'create') {
      tempTitle = 'Select baseline type';
      tempBtnText = 'Next';
    } else if (stage === 1 && modalType === 'manage') {
      tempTitle = 'Manage baselines';
      tempShowButtons = false;
    } else if (stage === 2 && baseline.baselineTypeId === BASELINE_TYPE.TIME_WELL) {
      tempTitle = 'Time/Well baseline ';
    } else if (stage === 2 && baseline.baselineTypeId === BASELINE_TYPE.TARGET) {
      tempTitle = 'Target baseline ';
    }

    return {
      title: tempTitle,
      showButtons: tempShowButtons,
      btnText: tempBtnText
    };
  }, [stage, modalType, baseline.baselineTypeId]);

  const updateTargetBaselineFieldsForDb = (newBaseline) => {
    REQUIRED_TARGET_FIELDS.forEach(({ fieldName, unit }) => {
      let value = parseFloat(newBaseline[fieldName]);
      let isDefaultValue = false;
      if (newBaseline.defaultTargetValues && newBaseline.useTargetDefaults.includes(fieldName)) {
        isDefaultValue = true;
        value = newBaseline.defaultTargetValues[fieldName];
      }
      if (isNumeric(value)) {
        if (unit === DISPLAY_UNIT.ENERGY) {
          value *= 1000; // Convert to KWh
        }
        if (isDefaultValue) {
          // apply percent change if its a default value
          value = value + (value * baseline.percentChange) / 100;
        }
        newBaseline[fieldName] = roundToSixDecimals(value);
      } else {
        newBaseline[fieldName] = 0;
      }
    });
    return newBaseline;
  };

  const handleSave = async () => {
    if (stage === 1 && modalType === 'create') {
      setStage(2);
      return;
    }
    if (!checkValidBaselineDetails(baseline)) {
      setHighlightErrors(true);
      return;
    }
    setLoading(true);
    Swal.fire({
      title: 'Saving...',
      background: theme.palette.neutral.neutral01,
      color: theme.palette.text.primary
    });
    Swal.showLoading();

    if (stage === 2 && modalType === 'create') {
      let start = moment.utc(baseline.startDate).startOf('day').format(UTC_TIMESTAMP);
      let end = moment.utc(baseline.endDate).endOf('day').format(UTC_TIMESTAMP);

      let newBaseline = {
        ...baseline,
        rank: rank,
        userId: userId,
        companyId: null
      };
      if (baseline.baselineTypeId === BASELINE_TYPE.TIME_WELL) {
        newBaseline = {
          ...newBaseline,
          startDate: start,
          rigId: baseline.selectedRig.rigId,
          endDate: end
        }; // Convert to KWh to match rest of DB
      } else {
        // Api requires a rigId and period. So these are just random. The rigId needs to be a valid rig though
        newBaseline = {
          ...newBaseline,
          startDate: moment().subtract(1, 'month').format(UTC_TIMESTAMP),
          endDate: moment().format(UTC_TIMESTAMP),
          rigId: baseline.selectedRig?.rigId ?? mainRig?.rigId 
        };
        // Loop through all default values and update by percentChange. Then convert to six decimals
        newBaseline = updateTargetBaselineFieldsForDb(newBaseline);
      }
      await createBaseline(newBaseline)
        .then(async (resp) => {
          await fetchBaselineById({ baselineId: resp.baselineId }).then((response) => {
            dispatch(addBaseline(response));
            dispatch(setSelectedBaseline(response));
          });
          toast.success(`Baseline created`);
          handleClose();
        })
        .catch(() => {
          toast.error(`Unable to create baseline`);
        })
        .finally(() => {
          setLoading(false);
          Swal.close();
        });
    }
    if (stage === 2 && modalType === 'manage') {
      let newBaseline = { ...baseline };
      if (baseline.baselineTypeId === BASELINE_TYPE.TARGET) {
        newBaseline = {
          ...newBaseline,
          startDate: moment().subtract(1, 'month').format(UTC_TIMESTAMP),
          endDate: moment().format(UTC_TIMESTAMP),
          rigId: baseline.selectedRig?.rigId ?? mainRig?.rigId
        };
        // Loop through all default values and update by percentChange. Then convert to six decimals
        newBaseline = updateTargetBaselineFieldsForDb(newBaseline);
      }

      await updateBaseline(newBaseline)
        .then(async () => {
          await fetchBaselineById({ baselineId: baseline.baselineId }).then((response) => {
            dispatch(updateBaselineDetails(response));
            dispatch(setSelectedBaseline(response));
          });
          toast.success(`Baseline updated`);
          handleClose();
        })
        .catch(() => {
          toast.error(`Unable to create baseline`);
        })
        .finally(() => {
          Swal.close();
          setLoading(false);
        });
    }
  };
  if (loading) {
    return <></>;
  }
  return (
    <Modal
      sx={{ visibility: isOpen ? 'visible' : 'hidden' }}
      DialogContentProps={{ borderBottom: 'none' }}
      dividers={true}
      DialogActionsProps={{ sx: { justifyContent: 'center' } }}
      actions={
        showButtons
          ? [
              <Button
                sx={{ color: (theme) => theme.palette.secondary.contrastText }}
                key={'cancel-button'}
                variant={'text'}
                onClick={handleClose}>
                Cancel
              </Button>,
              <Button key={'save-button'} onClick={handleSave}>
                {btnText}
              </Button>
            ]
          : null
      }
      closeIcon={<CloseIcon fontSize={'small'} />}
      PaperProps={{
        sx: {
          background: ({ palette }) => palette.neutral.neutral01
        }
      }}
      size={stage === 2 && baseline?.baselineTypeId === BASELINE_TYPE.TARGET ? 'lg' : 'sm'}
      open
      handleClose={(event, reason) => {
        if (reason === 'backdropClick') return; // Prevent closing when clicking outside
        handleClose();
      }}
      title={title}>
      <Box sx={{ p: 2, maxHeight: '60vh', minHeight: '250px' }}>
        {stage === 1 && modalType === 'create' && (
          <CreateBaselineType setBaseline={setBaseline} baseline={baseline} />
        )}
        {stage === 1 && modalType === 'manage' && (
          <ManageBaselines
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setBaseline={setBaseline}
            setStage={setStage}
            baseline={baseline}
          />
        )}
        {stage === 2 && (
          <BaselineDetails
            highlightErrors={highlightErrors}
            setBaseline={setBaseline}
            baseline={baseline}
          />
        )}
      </Box>
    </Modal>
  );
}

BaselineModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  modalType: PropTypes.oneOf(['create', 'manage']).isRequired
};

BaselineModal.defaultProps = {};

export default BaselineModal;
